// Final step — Circle: Your Training & Onboarding Hub.
// Runs AFTER Finish: onboarding paperwork is done, so this hands the agent off
// to Circle, where the training checklist and materials live. The Circle invite
// itself is sent by the `circle` step-2c provisioning action
// (lib/onboarding-actions.js), which is why the copy leads with "watch your
// inbox" — the account doesn't exist until they accept that invite.
//
// The walkthrough video is served from /assets/circle-intro.mp4. Unlike the
// 153MB welcome video (gitignored, CDN-hosted in prod), this clip is ~4MB, so
// it's committed and rides the git→Render pipeline — no CDN, no Drive sharing
// to get wrong. It replaced a Google Drive /preview embed, which rendered a
// sign-in wall for anyone outside the workspace. Override with
// VITE_CIRCLE_VIDEO_URL if it ever needs to move to a host.

import React from "react";
import { IcCheck, IcExternal, IcMail } from "./shared.jsx";
import { StepHeader, FooterNav } from "./steps_early.jsx";

const CIRCLE_URL = import.meta.env.VITE_CIRCLE_URL || "https://everyplace.circle.so/c/onboarding";
const VIDEO_URL = import.meta.env.VITE_CIRCLE_VIDEO_URL || "/assets/circle-intro.mp4";

// The Circle invite is sent to the agent's LAUGHTON TEAM address, not the
// personal one they applied with — so setting that mailbox up is step one, not
// an aside. The previous copy opened with "keep an eye on your inbox" without
// saying which inbox, which left recruits watching a personal account that was
// never going to receive anything.
function stepsToJoin({ ltEmail, mailboxPassword }) {
  return [
    {
      icon: <IcMail size={12}/>,
      text: (
        <>Set up your Laughton Team email first — your Circle invitation is sent there,
          not to your personal address.{" "}
          {ltEmail
            ? <>Sign in at <a href="https://mail.google.com" target="_blank" rel="noopener noreferrer"
                style={{ color: "var(--ep-sunset)" }}>mail.google.com</a> as{" "}
                <strong style={{ wordBreak: "break-word" }}>{ltEmail}</strong>
                {mailboxPassword && <> with the password <Cred>{mailboxPassword}</Cred></>}.</>
            : <>We're creating your @laughtonteam.com address now and will confirm it shortly.</>}
        </>
      ),
    },
    {
      icon: <IcCheck size={12}/>,
      text: <>You'll be asked to choose your own password straight away — the starting one
            works for this first sign-in only. Pick something you'll remember; this is the
            mailbox your paperwork and client mail come to.</>,
    },
    {
      icon: <IcMail size={12}/>,
      text: <>Once you're in, look for the <strong>invitation to join Circle</strong> in that inbox.
            Check spam if it isn't there — it arrives within a few minutes of finishing onboarding.</>,
    },
    {
      icon: <IcCheck size={12}/>,
      text: <>Use the invitation link to create your Circle account, and save that password
            once it's set.</>,
    },
    {
      icon: <IcCheck size={12}/>,
      text: <>Once logged in, navigate to the <strong>Laughton Team</strong> section in the menu on the
            left side of the Home screen — that's where your onboarding checklist and
            training materials live.</>,
    },
  ];
}

// Monospaced so a starting password can be transcribed without ambiguity —
// the difference between l, 1 and I matters when it is being retyped.
function Cred({ children }) {
  return (
    <code style={{
      font: "600 13px/1.4 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      background: "var(--ep-saguaro-blossom)", border: "1px solid var(--ep-border)",
      borderRadius: 2, padding: "1px 6px", whiteSpace: "nowrap",
    }}>{children}</code>
  );
}

function openCircle() {
  window.open(CIRCLE_URL, "_blank", "noopener,noreferrer");
}

function StepCircle({ agent, gate, onBack }) {
  const first = (agent?.name || "").split(" ")[0] || "there";
  // Both come from GET /v1/onboarding-gate/status, so the address shown is the
  // one the mailbox was actually created at and the password can be rotated
  // server-side without a frontend deploy.
  const steps = stepsToJoin({ ltEmail: gate?.ltEmail, mailboxPassword: gate?.mailboxPassword });

  return (
    <div style={{ maxWidth: 800 }}>
      <StepHeader
        eyebrow="Circle"
        title="Your training & onboarding hub."
        subtitle={`Your paperwork is done, ${first}. Training happens in Circle — here's how to get in and where to go once you're there.`}
      />

      {/* Walkthrough video */}
      <div style={{ marginBottom: 32, border: "1px solid var(--ep-border)", borderRadius: 2,
        overflow: "hidden", background: "#000" }}>
        <video
          controls
          playsInline
          preload="metadata"
          style={{ display: "block", width: "100%", maxHeight: "62vh" }}
          src={VIDEO_URL}
        >
          Your browser can't play this video — the Circle link below still works.
        </video>
      </div>

      {/* How to get in */}
      <div className="ep-card" style={{ padding: 28, marginBottom: 24 }}>
        <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)", marginBottom: 16 }}>
          Getting into Circle
        </div>
        <div className="vstack" style={{ gap: 14 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 22, height: 22, borderRadius: 999, background: "var(--ep-avocado)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--ep-origin)", flexShrink: 0, marginTop: 1 }}>
                {s.icon}
              </div>
              <span style={{ fontSize: 14, color: "var(--ep-origin)", lineHeight: 1.55 }}>{s.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Circle CTA */}
      <div style={{ padding: "22px 28px", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)",
        borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 20, flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <div className="ep-eyebrow" style={{ color: "rgba(255,245,228,0.75)" }}>Circle</div>
          <div style={{ font: "300 20px/1.2 var(--ep-font-display)", margin: "6px 0 4px" }}>
            Your Training &amp; Onboarding Hub
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,245,228,0.75)", wordBreak: "break-word" }}>
            {CIRCLE_URL.replace(/^https?:\/\//, "")}
          </div>
        </div>
        <button className="ep-btn primary"
          style={{ background: "var(--ep-high-noon)", color: "var(--ep-origin)" }}
          onClick={openCircle}>
          Open Circle&nbsp;<IcExternal size={14}/>
        </button>
      </div>
      <p style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginBottom: 32 }}>
        Haven't received your invitation yet? It's sent automatically once onboarding
        completes — give it a few minutes, check your spam folder, then reach out to{" "}
        <a href="mailto:onboard@everyplace.com" style={{ color: "var(--ep-sunset)" }}>onboard@everyplace.com</a>.
      </p>

      <FooterNav onBack={onBack} onNext={openCircle} nextLabel="Open Circle"/>
    </div>
  );
}

export { StepCircle };
